import { Actions } from '@space-core/datahub'
import { Entities } from '@space-core/datahub'
import DataHubService from '@space-core/datahub/hotmartClub/dataHubService'
import { PixelGroup, PixelPhase, SuperPixelUtils } from '@space-core/utils/superPixel'
import { freeSignUpEventDispatch } from '@space-events/freesignup'
import { loginEventDispatch } from '@space-events/login'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import type { Product, SectionProductDetail } from '@space-shell/common/services/sections'
import { useAuthStore, useMembershipStore } from '@space-shell/core/store'
import {
  getCouponStudent,
  getProductCoupon,
  isHubUrl,
  EMPTY_COUPON
} from '@space-shell/modules/products/utils/productCoupon'
import { useNavigate } from 'react-router-dom'

import type { ProductCardProps } from './type'

import { ClickHomeKnowProduct } from '../../datahub/schemas/click/product/clickHomeKnowProduct'

const useProductClick = ({ product, sectionId }: ProductCardProps) => {
  const navigate = useNavigate()
  const { authStatus } = useAuthContext()
  const { sendDataHubEvent } = useDataHubEvents()
  const { membershipSlug, membershipData } = useMembershipStore()
  const { id: userId } = useAuthStore()

  const { id, freeSignUp, joined, purchased } = {
    id: (product as Product).itemId || (product as SectionProductDetail).id,
    ...product
  }

  const handleSendDataHubEvent = ({ name }: { name: Actions }) => {
    sendDataHubEvent({
      group: Entities.PRODUCT,
      name,
      params: {
        product: {
          id: Number(id),
          name: product.category || '',
          purchased,
          freeSignup: freeSignUp
        }
      }
    })
  }

  const handleSendEventKnowProduct = (url: string, isRedirectClubProductPage = false) => {
    const isStudentCoupon = !!getCouponStudent(product)
    const productCoupon = getProductCoupon(product)?.toString() || EMPTY_COUPON

    const clickSchema = new ClickHomeKnowProduct({
      productId: id?.toString() || '',
      studentDiscount: isStudentCoupon ? productCoupon : EMPTY_COUPON,
      nonStudentDiscount: isStudentCoupon ? EMPTY_COUPON : productCoupon,
      isStudent: isStudentCoupon,
      isRedirectToProductDetails: isHubUrl(url),
      isRedirectClubProductPage
    })

    DataHubService.sendEvent({ schema: clickSchema })
  }

  const handleSendPixelEvent = ({ url }: { url: string }) => {
    SuperPixelUtils.sendEvent({
      ownerUcode: String(membershipData?.ownerUcode),
      instanceId: membershipSlug,
      group: product.purchased ? PixelGroup.PreConsumptionEvents : PixelGroup.SalesEvents,
      phase: product.purchased ? PixelPhase.ProductPurchasedClick : PixelPhase.ProductClick,
      runtimeValues: {
        slug: membershipSlug,
        productId: id,
        productName: (product as Product).title ?? '',
        userId,
        ...(!product.purchased && { price: product.price ?? '', url })
      }
    })
  }

  const handleProductClick = (event: React.MouseEvent<HTMLElement, MouseEvent>, url: string) => {
    event.stopPropagation()

    const isProductPageClick = url.match(
      new RegExp(
        `${window?.location?.pathname}/[a-z_-]+/(?=.*[A-Z])(?=.*[0-9])[A-Z0-9]+(?:\\?.*)?$`
      )
    )

    handleSendPixelEvent({ url })

    if (freeSignUp && !joined) {
      handleSendDataHubEvent({ name: Actions.CLICK_PRODUCT_FREE_SIGNUP })

      if (authStatus === AuthStatus.AUTHENTICATED) {
        freeSignUpEventDispatch({ productId: Number(id), sectionId: sectionId })
      } else {
        loginEventDispatch({ signInRedirect: true })
      }

      return
    }

    if (url.includes('http')) {
      handleSendDataHubEvent({ name: Actions.CLICK_PRODUCT_URL })
      handleSendEventKnowProduct(url)

      window.open(url, '_blank')

      return
    }

    if (isProductPageClick) {
      handleSendDataHubEvent({ name: Actions.CLICK_PRODUCT_PRODUCT_PAGE })
      handleSendEventKnowProduct(url, true)
      navigate(url)

      return
    }

    handleSendDataHubEvent({ name: Actions.CLICK_PRODUCT })
    navigate(url)
  }

  return handleProductClick
}

export default useProductClick
